import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type { EnumMetaType, ModelMetaType } from '../../types/metaModel'
import type { TemplateReferenceValueSetupField } from '../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'

export const useUnusedMappingKeys = (
  field: TemplateReferenceValueSetupField,
) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const mappingType = templateVersion?.modelType?.typeName

  assertNotEmpty(mappingType)

  const mappingModelTypeName = mappingType.replace(/Mapping$/, '')
  const mappingModelType = metaModel.types.get(
    mappingModelTypeName,
  ) as Optional<ModelMetaType>

  const keyEnumType = mappingModelType?.properties.find((property) =>
    property.stereoTypes.includes('mappingFunctionalityKey'),
  )?.type as Optional<EnumMetaType>

  assertNotEmpty(keyEnumType)

  const configuredEnumDefinition = applicationEnumDefinitions?.get(
    keyEnumType.typeName,
  )
  const allLiterals = useMemo(
    () =>
      (configuredEnumDefinition
        ? configuredEnumDefinition.literals
            .map((enumLiteral) =>
              enumLiteral.type === 'modeled'
                ? enumLiteral.literalName
                : enumLiteral.internalOperation.entity?.name,
            )
            .filter(notEmpty)
        : keyEnumType.values) ?? [],
    [configuredEnumDefinition, keyEnumType.values],
  )

  const existentLiterals = useMemo(
    () => field.value?.variants.map((variant) => variant.key) ?? [],
    [field.value?.variants],
  )

  return allLiterals.filter(
    (literalName) => !existentLiterals.includes(literalName),
  )
}
